import { LOGIN_SUCCESS, LOGOUT } from '@comeon/actions-public';
import reducerRegistry from '@comeon/reducer-registry';

import { reportException } from '@comeon/error-reporter';
import { ErrorReporterDomain } from '../../error-handling/error-reporter-domain';
import { activeFiltersSelector } from '~/components/casino-shared/ducks/selectors';
import { updateJackpotTiers } from '~/components/casino-shared/inhouse-jackpot/ducks';
import { BaseResponse, requestApi } from '~/services/api-new';

import { EnhancedGame, Filters, LFSJackpotResponse, LovedAndRecent, SocketJackpot } from '../types';
import {
	ADD_LOCAL_RECENTLY_PLAYED,
	JACKPOT_SOCKET_CLOSE_CONNECTION,
	JACKPOT_SOCKET_ON_CONNECTION_OPEN,
	JACKPOT_SOCKET_ON_MESSAGE,
	JACKPOT_SOCKET_SET_RETRY_TIMEOUT,
	RESET_ACTIVE_FILTERS,
	RESET_ACTIVE_SORTING,
	SET_ACTIVE_FILTERS,
	SET_ACTIVE_SORTING,
	SET_IS_BALANCE_LIMIT_EXCEEDED_MODAL_SHOWN,
	SET_IS_INSUFFICIENT_CASINO_BALANCE_MODAL_SHOWN,
	SET_IS_ZERO_CASINO_BALANCE_MODAL_SHOWN,
	SET_JACKPOT_DATA,
	SET_LOVED_AND_RECENT,
	SET_SWITCH_TO_REAL,
	UPDATE_LIKES_INFO
} from './action-names';
import type { CasinoSharedAction, CasinoSharedState, CasinoSharedThunkAction, ParsedSocketJackpot } from './types';

const REDUCER_NAME = 'casinoShared';
const host = '/';

const JACKPOT_SOCKET_RETRY_TIME = 5000;

const recentlyPlayedGame =
	(gameCode: string): CasinoSharedThunkAction =>
	async () => {
		await requestApi({
			endpoint: `${host}rest/mygames/recentlyplayedgames`,
			payload: {
				string: gameCode
			},
			method: 'PUT'
		});
	};

const fetchLovedAndRecentGames = (): CasinoSharedThunkAction => async (dispatch, getState) => {
	const state = getState();

	const {
		casinoShared: { lovedAndRecentHydrated }
	} = state;

	if (lovedAndRecentHydrated) {
		return;
	}

	const response = await requestApi<BaseResponse<LovedAndRecent>>({
		endpoint: `${host}rest/state/casino/lovedAndRecentlyPlayed`
	});

	if (response.status === 'FAILURE') {
		return;
	}

	dispatch({
		type: SET_LOVED_AND_RECENT,
		payload: { lovedAndRecent: response.result }
	});
};

const toggleLoveGame =
	(gameCode: string): CasinoSharedThunkAction =>
	async (dispatch, getState) => {
		const state = getState();

		const {
			casinoShared: { lovedAndRecent }
		} = state;

		const loved = !lovedAndRecent.loved.includes(gameCode);

		dispatch({
			type: UPDATE_LIKES_INFO,
			payload: { gameCode, loved }
		});

		await requestApi({
			endpoint: `${host}rest/mygames/lovedgames`,
			payload: { string: gameCode },
			method: loved ? 'PUT' : 'DELETE'
		});
	};

const toggleFilter =
	(filter: string, type: keyof Filters): CasinoSharedThunkAction =>
	(dispatch, getState) => {
		const state = getState();
		const activeFilters = activeFiltersSelector(state);
		const newActiveFilters = {
			...activeFilters,
			[type]: activeFilters[type].includes(filter)
				? activeFilters[type].filter(activeFilter => filter !== activeFilter)
				: activeFilters[type].concat(filter)
		};

		dispatch({
			type: SET_ACTIVE_FILTERS,
			payload: newActiveFilters
		});
	};

const applyTemporaryFilters =
	(tempFilters: Filters): CasinoSharedThunkAction =>
	dispatch => {
		dispatch({
			type: SET_ACTIVE_FILTERS,
			payload: tempFilters
		});
	};

const clearFilters = (): CasinoSharedThunkAction => dispatch => {
	dispatch({
		type: RESET_ACTIVE_FILTERS
	});
};

const setActiveSorting =
	(option: number): CasinoSharedThunkAction =>
	dispatch => {
		dispatch({
			type: SET_ACTIVE_SORTING,
			payload: option
		});
	};

const clearSorting = (): CasinoSharedThunkAction => dispatch => {
	dispatch({
		type: RESET_ACTIVE_SORTING
	});
};

const addLocalRecentlyPlayed = (game: EnhancedGame) => ({
	type: ADD_LOCAL_RECENTLY_PLAYED,
	payload: game
});

const toggleSwitchToRealMode = (payload: boolean) => ({
	type: SET_SWITCH_TO_REAL,
	payload
});

const fetchJackpotData = (): CasinoSharedThunkAction => async (dispatch, getState) => {
	const state = getState();

	if (Object.keys(state.casinoShared.jackpotData).length) return;

	try {
		const response = await requestApi<LFSJackpotResponse>({
			endpoint: `${host}live-feed-service/jackpot?currency=${state.account.currency}&franchiseId=${state.brand.franchiseId}`
		});

		const parsedJackpotData = response.reduce((acc, jackpot) => {
			const gamesWithJackpot = jackpot.gameNames.reduce((gamesWithAmount, game) => {
				return {
					...gamesWithAmount,
					[game]: {
						amount: jackpot.amount,
						provider: jackpot.productCode
					}
				};
			}, {});

			return { ...acc, ...gamesWithJackpot };
		}, {});

		dispatch({
			type: SET_JACKPOT_DATA,
			payload: parsedJackpotData
		});
	} catch (error) {
		dispatch({
			type: SET_JACKPOT_DATA,
			payload: {}
		});
	}
};
export const setIsInsufficientCasinoBalanceModalShown = (payload: boolean) => {
	return {
		type: SET_IS_INSUFFICIENT_CASINO_BALANCE_MODAL_SHOWN,
		payload
	};
};

export const setIsZeroCasinoBalanceModalShown = (payload: boolean) => {
	return {
		type: SET_IS_ZERO_CASINO_BALANCE_MODAL_SHOWN,
		payload
	};
};

export const setIsBalanceLimitExceededModalShown = (payload: boolean) => {
	return {
		type: SET_IS_BALANCE_LIMIT_EXCEEDED_MODAL_SHOWN,
		payload
	};
};

const createRetryTimeout = (
	dispatch: Parameters<CasinoSharedThunkAction>['0'],
	getState: Parameters<CasinoSharedThunkAction>['1'],
	retryFn: () => void,
	timeoutType: 'inHouseJackpotSocketTimeout' | 'jackpotSocketTimeout',
	retryTime = JACKPOT_SOCKET_RETRY_TIME
) => {
	if (!getState()[REDUCER_NAME][timeoutType]) {
		const retryTimeout = setTimeout(retryFn, retryTime);

		dispatch({
			type: JACKPOT_SOCKET_SET_RETRY_TIMEOUT,
			payload: { retryTimeout, timeoutType }
		});
	}
};

const establishJackpotWebSocketConnection =
	({ isInHouse = false, url }: { isInHouse?: boolean; url: string }): CasinoSharedThunkAction =>
	(dispatch, getState) => {
		const conn = new WebSocket(url);
		const socketType = isInHouse ? 'inHouseJackpotSocket' : 'jackpotSocket';
		const timeoutType = `${socketType}Timeout` as const;

		conn.addEventListener('error', () => {
			if (window[socketType]) {
				// remove socket instance when connection is closed
				window[socketType] = null;
			}

			// retry in JACKPOT_SOCKET_RETRY_TIME
			createRetryTimeout(
				dispatch,
				getState,
				() => {
					dispatch(establishJackpotWebSocketConnection({ isInHouse, url }));
					dispatch({
						type: JACKPOT_SOCKET_SET_RETRY_TIMEOUT,
						payload: { retryTimeout: null, timeoutType }
					});
				},
				timeoutType,
				JACKPOT_SOCKET_RETRY_TIME
			);
		});

		conn.addEventListener('open', () => {
			dispatch({
				type: JACKPOT_SOCKET_ON_CONNECTION_OPEN,
				payload: {
					conn,
					socketType
				}
			});
		});

		conn.addEventListener('message', (messageEvent: { data?: string }) => {
			try {
				if (!messageEvent?.data) return;

				const data = JSON.parse(messageEvent.data) as SocketJackpot[];

				dispatch({
					type: JACKPOT_SOCKET_ON_MESSAGE,
					payload: { data, isInHouse }
				});

				if (isInHouse && data?.[0]?.pools) {
					dispatch(updateJackpotTiers(data?.[0]?.pools));
				}
			} catch (e) {
			console.warn(e); // eslint-disable-line
				if (e instanceof Error) {
					reportException(e, {
						domain: ErrorReporterDomain.CASINO,
						extra: {
							type: 'casino-jackpot-socket-parse-error',
							url: window.location
						}
					});
				}

				window.isJackpotWebsocketErrorSent = true;
			}
		});

		conn.addEventListener('close', closeEvent => {
			if (window[socketType]) {
				// remove jackpot Socket when the connection is closed
				window[socketType] = null;
			}

			if (closeEvent.code === 1000) {
				// no action
			} else {
				// retry
				createRetryTimeout(
					dispatch,
					getState,
					() => {
						dispatch(establishJackpotWebSocketConnection({ isInHouse, url }));
						dispatch({
							type: JACKPOT_SOCKET_SET_RETRY_TIMEOUT,
							payload: { retryTimeout: null, timeoutType }
						});
					},
					timeoutType,
					JACKPOT_SOCKET_RETRY_TIME
				);
			}
		});
	};

const closeJackpotWebSocketConnection = ({ isInHouse = false }) => ({
	type: JACKPOT_SOCKET_CLOSE_CONNECTION,
	payload: { socketType: isInHouse ? 'inHouseJackpotSocket' : 'jackpotSocket' }
});

const createJackpotDataFromSocketMessage = (
	message: SocketJackpot[],
	isInHouse: boolean
): Record<string, ParsedSocketJackpot> =>
	message.reduce<Record<string, ParsedSocketJackpot>>((prev, curr) => {
		if (curr.pools?.length) {
			return {
				...prev,
				...(curr.amount && {
					[isInHouse ? curr.id : curr.name]: {
						amount: curr.amount,
						targetTime: curr.targetTime
					}
				}),
				...createJackpotDataFromSocketMessage(curr.pools, isInHouse)
			};
		}

		return {
			...prev,
			[curr.name]: {
				amount: curr.amount,
				targetTime: curr.targetTime
			}
		};
	}, {});

const initialState: CasinoSharedState = {
	lovedAndRecentHydrated: false,
	lovedAndRecent: { loved: [], recent: [] },
	localRecentlyPlayed: {},
	switchToRealMode: false,
	jackpotData: {},
	isInsufficientCasinoBalanceModalShown: false,
	isBalanceLimitExceededModalShown: false,
	isZeroCasinoBalanceModalShown: false,
	jackpots: {},
	inHouseJackpotSocketTimeout: null,
	jackpotSocketTimeout: null,
	activeFilters: {
		provider: [],
		feature: [],
		amount: []
	},
	activeSortingOption: 1
};

const reducer = (stateTemp = initialState, action: CasinoSharedAction) => {
	const state = stateTemp.initialized ? stateTemp : { ...initialState, ...stateTemp, initialized: true };
	switch (action.type) {
		case LOGIN_SUCCESS:
			return {
				...initialState,
				switchToRealMode: true
			};
		case LOGOUT:
			return {
				...initialState
			};
		case SET_LOVED_AND_RECENT:
			return { ...state, ...action.payload, lovedAndRecentHydrated: true };
		case UPDATE_LIKES_INFO: {
			let lovedCodes = [...state.lovedAndRecent.loved];
			if (action.payload.loved) {
				if (!lovedCodes.includes(action.payload.gameCode)) {
					lovedCodes = [...lovedCodes, action.payload.gameCode];
				}
			} else {
				lovedCodes = lovedCodes.filter(code => code !== action.payload.gameCode);
			}

			return {
				...state,
				lovedAndRecent: {
					...state.lovedAndRecent,
					loved: lovedCodes
				}
			};
		}
		case ADD_LOCAL_RECENTLY_PLAYED:
			return {
				...state,
				localRecentlyPlayed: {
					[action.payload.code]: action.payload,
					...state.localRecentlyPlayed
				}
			};

		case SET_SWITCH_TO_REAL:
			return {
				...state,
				switchToRealMode: action.payload
			};
		case SET_JACKPOT_DATA:
			return {
				...state,
				jackpotData: action.payload
			};
		case SET_IS_INSUFFICIENT_CASINO_BALANCE_MODAL_SHOWN:
			return { ...state, isInsufficientCasinoBalanceModalShown: action.payload };
		case SET_IS_BALANCE_LIMIT_EXCEEDED_MODAL_SHOWN:
			return { ...state, isBalanceLimitExceededModalShown: action.payload };
		case SET_IS_ZERO_CASINO_BALANCE_MODAL_SHOWN:
			return { ...state, isZeroCasinoBalanceModalShown: action.payload };
		case JACKPOT_SOCKET_ON_MESSAGE: {
			const { data, isInHouse } = action.payload;
			const updatedJackpots = createJackpotDataFromSocketMessage(data, isInHouse);

			return {
				...state,
				jackpots: {
					...state.jackpots,
					...updatedJackpots
				}
			};
		}
		case JACKPOT_SOCKET_SET_RETRY_TIMEOUT:
			return {
				...state,
				[action.payload.timeoutType]: action.payload.retryTimeout
			};
		case JACKPOT_SOCKET_ON_CONNECTION_OPEN: {
			const { conn, socketType } = action.payload;

			if (window[socketType]) {
				window[socketType].close();
			}
			window[socketType] = conn;
			return state;
		}
		case JACKPOT_SOCKET_CLOSE_CONNECTION: {
			const { socketType } = action.payload;

			if (window[socketType]) {
				window[socketType].close(1000);
			}
			window[socketType] = null;
			return state;
		}
		case SET_ACTIVE_FILTERS: {
			return { ...state, activeFilters: action.payload };
		}
		case RESET_ACTIVE_FILTERS: {
			return { ...state, activeFilters: initialState.activeFilters };
		}
		case SET_ACTIVE_SORTING: {
			return { ...state, activeSortingOption: action.payload };
		}
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

export {
	addLocalRecentlyPlayed,
	applyTemporaryFilters,
	clearFilters,
	clearSorting,
	closeJackpotWebSocketConnection,
	establishJackpotWebSocketConnection,
	fetchJackpotData,
	fetchLovedAndRecentGames,
	recentlyPlayedGame,
	setActiveSorting,
	toggleFilter,
	toggleLoveGame,
	toggleSwitchToRealMode
};

export default reducer;
